import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Refresh, Download, UploadFile, Build } from '@mui/icons-material';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import { useMaintenancePermissions } from '../maintenance/hooks/useMaintenancePermissions';
import StoreActions from '../maintenance/StoreActions';
import RequiredSparesManager from '../maintenance/components/RequiredSparesManager';
import RequiredConsumablesManager from '../maintenance/components/RequiredConsumablesManager';
import RequiredToolsEquipmentManager from '../maintenance/components/RequiredToolsEquipmentManager';
import { normalizePartNumber } from '../../utils/partNumber';
import { getWorkOrderDisplayNumber, getWorkOrderCandidateNumber } from '../../utils/workOrderDisplayNumber';
import {
  getStoreIntentLastUpdatedTime,
  isStoreIntentCompleted
} from '../../utils/workOrderStoreReview';
import { useWorkOrderDisplayContext } from '../maintenance/hooks/useWorkOrderDisplayContext';

const countWorkOrderSparesConsumables = (wo) =>
  (wo?.requiredSpares?.length || 0) + (wo?.requiredConsumables?.length || 0);

const countWorkOrderTools = (wo) => wo?.requiredToolsEquipment?.length || 0;

const countWorkOrderComponents = (wo) =>
  countWorkOrderSparesConsumables(wo) + countWorkOrderTools(wo);

const hasProcurementSnapshot = (wo) =>
  Boolean(wo?.partsProcurementRequirements?.partNumbers?.length);

const hasMaterialIntent = (wo) => hasProcurementSnapshot(wo) || countWorkOrderComponents(wo) > 0;

/** Build PurchaseOrderForm initialData from planning snapshot (quantities to procure only). */
function buildPoPrefillFromPlanningShortage(wo, woLabel = '') {
  const req = wo.partsProcurementRequirements;
  if (!req?.partNumbers?.length) return null;
  const label = woLabel || wo.workOrderNumber || wo.mroJobNumber || wo.title || '';
  const lineItems = [];
  for (let i = 0; i < req.partNumbers.length; i += 1) {
    const toProcure = Number(req.quantitiesToProcure?.[i]);
    if (!Number.isFinite(toProcure) || toProcure <= 0) continue;
    lineItems.push({
      lineNumber: lineItems.length + 1,
      partNumber: normalizePartNumber(req.partNumbers[i]),
      description: String(req.descriptions?.[i] || '').trim(),
      quantity: toProcure,
      unit: 'EACH',
      remarks: `Shortage — WO ${label}`.trim()
    });
  }
  if (!lineItems.length) return null;
  const aircraftId = wo.aircraftId?._id || wo.aircraftId || '';
  const notesParts = [
    `Maintenance work order: ${label}`,
    req.specifications ? `Planning specifications:\n${req.specifications}` : ''
  ].filter(Boolean);
  return {
    orderTo: '',
    billTo: '',
    shipTo: '',
    operatorId: '',
    aircraftId,
    aircraftRegistration: wo.aircraftId?.registration || '',
    aircraftModelVariant: wo.aircraftId?.model || '',
    aircraftSerialNumber: wo.aircraftId?.serialNumber || '',
    expectedDeliveryDate: '',
    priority: ['CRITICAL', 'HIGH', 'MEDIUM'].includes(wo.priority) ? wo.priority : 'MEDIUM',
    notes: notesParts.join('\n\n'),
    lineItems
  };
}

function buildPoPrefillFromIndependentIntent(item) {
  const req = item?.partsProcurementRequirements;
  if (!req?.partNumbers?.length) return null;
  const lineItems = req.partNumbers.map((pn, i) => ({
    lineNumber: i + 1,
    partNumber: normalizePartNumber(pn),
    description: String(req.descriptions?.[i] || '').trim(),
    quantity: Number(req.quantitiesToProcure?.[i]) || 1,
    unit: 'EACH',
    remarks: 'Independent planning procurement request'
  }));
  if (!lineItems.length) return null;
  return {
    orderTo: '',
    billTo: '',
    shipTo: '',
    operatorId: '',
    aircraftId: item.aircraftId || 'GEN',
    aircraftRegistration: item.aircraftRegistration || '',
    aircraftModelVariant: item.aircraftModelVariant || '',
    aircraftSerialNumber: item.aircraftSerialNumber || '',
    expectedDeliveryDate: '',
    priority: item.priority || 'MEDIUM',
    notes: item.independentNotes || 'Independent planning procurement request (no WO link)',
    lineItems
  };
}

const CAN_CREATE_PO_ROLES = [
  ROLES.STORE_EXECUTIVE,
  ROLES.STORE_INSPECTOR,
  ROLES.ACCOUNTABLE_MANAGER,
  ROLES.SUPER_ADMIN
];

const getLastUpdatedTime = (wo) => getStoreIntentLastUpdatedTime(wo);

const PRIORITY_RANK = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };

/** Sort keys for INTENT request rows (table + planning snapshot aggregates). */
const INTENT_SORT_FIELDS = [
  { value: 'defaultQueue', label: 'Default (newest first)' },
  { value: 'workOrderNumber', label: 'WO #' },
  { value: 'title', label: 'Title' },
  { value: 'aircraftRegistration', label: 'Aircraft' },
  { value: 'lastUpdated', label: 'Last updated' },
  { value: 'scheduledDate', label: 'Scheduled date' },
  { value: 'dueDate', label: 'Due date' },
  { value: 'createdAt', label: 'Created' },
  { value: 'priority', label: 'Priority' },
  { value: 'status', label: 'Status' },
  { value: 'type', label: 'WO type' },
  { value: 'partLineCount', label: 'Part lines (count)' },
  { value: 'totalToProcure', label: 'Total to procure (sum)' },
  { value: 'maxLineToProcure', label: 'Largest line to procure' },
  { value: 'totalRequiredQty', label: 'Total required qty (sum)' },
  { value: 'totalAvailableSnapshot', label: 'Total available snapshot (sum)' },
  { value: 'firstPartNumber', label: 'First part number' },
  { value: 'firstLineDescription', label: 'First line description' },
  { value: 'partsProvidedAt', label: 'Planning parts submitted at' }
];

function formatIntentDate(value) {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime()) || date.getTime() <= 0) return '—';
  return date.toLocaleString();
}

function sumNumericArray(arr, positiveOnly = false) {
  if (!Array.isArray(arr)) return 0;
  return arr.reduce((acc, q) => {
    const n = Number(q);
    if (!Number.isFinite(n)) return acc;
    if (positiveOnly && n <= 0) return acc;
    return acc + n;
  }, 0);
}

function sumQuantitiesToProcure(wo) {
  return sumNumericArray(wo?.partsProcurementRequirements?.quantitiesToProcure, true);
}

const isIndependentIntent = (row) => row.intentType === 'PLANNING_NON_WO';

/** Stable filter key for INTENT rows (WO aircraft or independent registration/GEN). */
function getIntentAircraftFilterValue(wo) {
  const populated = wo?.aircraftId;
  if (populated && typeof populated === 'object' && populated._id) {
    return String(populated._id);
  }
  if (populated) {
    return String(populated);
  }
  const reg = String(wo?.aircraftRegistration || '').trim();
  if (reg) return `reg:${reg}`;
  return 'NONE';
}

function getIntentAircraftLabel(wo) {
  return wo?.aircraftId?.registration || wo?.aircraftRegistration || 'Unassigned';
}

function getIntentSortComparable(wo, field) {
  switch (field) {
    case 'workOrderNumber':
      return `${wo.mroJobNumber || ''} ${wo.workOrderNumber || ''}`.trim().toLowerCase();
    case 'title':
      return (wo.title || '').toLowerCase();
    case 'aircraftRegistration':
      return (wo.aircraftId?.registration || wo.aircraftRegistration || '').toLowerCase();
    case 'lastUpdated':
      return getLastUpdatedTime(wo);
    case 'scheduledDate':
      return wo.scheduledDate ? new Date(wo.scheduledDate).getTime() : null;
    case 'dueDate':
      return wo.dueDate ? new Date(wo.dueDate).getTime() : null;
    case 'createdAt':
      return wo.createdAt ? new Date(wo.createdAt).getTime() : null;
    case 'priority':
      return PRIORITY_RANK[wo.priority] ?? 99;
    case 'status':
      return (wo.status || '').toLowerCase();
    case 'type':
      return (wo.type || '').toLowerCase();
    case 'partLineCount':
      return wo.partsProcurementRequirements?.partNumbers?.length || 0;
    case 'totalToProcure':
      return sumQuantitiesToProcure(wo);
    case 'maxLineToProcure': {
      const arr = wo?.partsProcurementRequirements?.quantitiesToProcure;
      if (!Array.isArray(arr) || !arr.length) return 0;
      return arr.reduce((max, q) => {
        const n = Number(q);
        if (!Number.isFinite(n) || n <= 0) return max;
        return Math.max(max, n);
      }, 0);
    }
    case 'totalRequiredQty':
      return sumNumericArray(wo?.partsProcurementRequirements?.quantities);
    case 'totalAvailableSnapshot':
      return sumNumericArray(wo?.partsProcurementRequirements?.availableQuantities);
    case 'firstPartNumber': {
      const first = wo?.partsProcurementRequirements?.partNumbers?.[0];
      return (first != null ? String(first) : '').toLowerCase();
    }
    case 'firstLineDescription': {
      const d = wo?.partsProcurementRequirements?.descriptions?.[0];
      return (d != null ? String(d) : '').toLowerCase();
    }
    case 'partsProvidedAt':
      return wo.partsProcurementRequirements?.providedAt
        ? new Date(wo.partsProcurementRequirements.providedAt).getTime()
        : null;
    default:
      return null;
  }
}

function compareNullableNumbers(a, b, dir) {
  const aNull = a == null || Number.isNaN(a);
  const bNull = b == null || Number.isNaN(b);
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  const diff = a - b;
  return dir === 'asc' ? diff : -diff;
}

function compareStringsCaseInsensitive(a, b, dir) {
  const as = String(a ?? '');
  const bs = String(b ?? '');
  const c = as.localeCompare(bs, undefined, { sensitivity: 'base' });
  return dir === 'asc' ? c : -c;
}

function sortIntentQueue(rows, field, dir) {
  if (field === 'defaultQueue') {
    // Newest requirements first (latest come order).
    return [...rows].sort((a, b) => getLastUpdatedTime(b) - getLastUpdatedTime(a));
  }
  const isNumericField = [
    'lastUpdated',
    'scheduledDate',
    'dueDate',
    'createdAt',
    'priority',
    'partLineCount',
    'totalToProcure',
    'maxLineToProcure',
    'totalRequiredQty',
    'totalAvailableSnapshot',
    'partsProvidedAt'
  ].includes(field);

  return [...rows].sort((a, b) => {
    const va = getIntentSortComparable(a, field);
    const vb = getIntentSortComparable(b, field);
    if (isNumericField) {
      return compareNullableNumbers(va, vb, dir);
    }
    return compareStringsCaseInsensitive(va, vb, dir);
  });
}

const StoreWorkOrderRequests = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const displayContext = useWorkOrderDisplayContext();
  const woDisplayLabel = useCallback(
    (wo) => getWorkOrderDisplayNumber(wo, displayContext) || wo?.title || wo?._id || '',
    [displayContext]
  );
  const permissions = useMaintenancePermissions(user);
  const canCreatePo = CAN_CREATE_PO_ROLES.includes(user?.role);
  const [storePlanningQueue, setStorePlanningQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeActionWo, setStoreActionWo] = useState(null);
  const [pendingFilter, setPendingFilter] = useState('ALL');
  const [partRequiredFilter, setPartRequiredFilter] = useState('ALL');
  const [aircraftFilter, setAircraftFilter] = useState('ALL');
  const [updatedFrom, setUpdatedFrom] = useState('');
  const [updatedTo, setUpdatedTo] = useState('');
  const [sortField, setSortField] = useState('defaultQueue');
  const [sortDirection, setSortDirection] = useState('desc');
  const uploadCsvInputRef = useRef(null);
  const [uploadContext, setUploadContext] = useState(null);
  const [componentsDialogWo, setComponentsDialogWo] = useState(null);

  const openProcurementFormWithPrefill = useCallback(
    (prefill) => {
      navigate('/procurement', {
        state: {
          procurement: {
            nonce: Date.now(),
            initialData: prefill
          }
        }
      });
    },
    [navigate]
  );

  const parseCsvLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const escapeCsv = (value) => {
    const stringValue = String(value ?? '');
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const downloadRequirementsCsv = (wo) => {
    const req = wo?.partsProcurementRequirements;
    if (!req?.partNumbers?.length) return;
    const rows = req.partNumbers.map((pn, index) => ({
      partNumber: String(pn || '').trim(),
      quantityToProcure: Number(req.quantitiesToProcure?.[index]) || 0,
      requiredQuantity: Number(req.quantities?.[index]) || 0,
      availableQuantity: Number(req.availableQuantities?.[index]) || 0,
      description: String(req.descriptions?.[index] || '').trim()
    }));
    const csvLines = [
      ['Part Number', 'Quantity To Procure', 'Required Quantity', 'Available Quantity', 'Description'].join(','),
      ...rows.map((row) => [
        escapeCsv(row.partNumber),
        escapeCsv(row.quantityToProcure),
        escapeCsv(row.requiredQuantity),
        escapeCsv(row.availableQuantity),
        escapeCsv(row.description)
      ].join(','))
    ];
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `procurement-shortage-${woDisplayLabel(wo)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handleUploadClick = (wo) => {
    setUploadContext(wo);
    uploadCsvInputRef.current?.click();
  };

  const handleUploadCsv = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file || !uploadContext) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length < 2) {
        showToast('CSV has no data rows to import', 'error');
        return;
      }
      const header = parseCsvLine(lines[0]).map((h) => h.toLowerCase().trim());
      const pnIdx = header.findIndex((h) => h === 'part number' || h === 'partnumber' || h === 'pn');
      const qtyIdx = header.findIndex((h) => h === 'quantity to procure' || h === 'quantity' || h === 'qty');
      const descIdx = header.findIndex((h) => h === 'description' || h === 'desc');
      if (pnIdx < 0 || qtyIdx < 0) {
        showToast('CSV must include "Part Number" and "Quantity To Procure" columns', 'error');
        return;
      }
      const lineItems = lines.slice(1).map((line) => parseCsvLine(line)).map((columns) => {
        const partNumber = normalizePartNumber(columns[pnIdx]);
        const quantity = Number(columns[qtyIdx]);
        const description = descIdx >= 0 ? String(columns[descIdx] || '').trim() : '';
        return {
          partNumber,
          quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
          description
        };
      }).filter((row) => row.partNumber);
      if (!lineItems.length) {
        showToast('No valid part rows found in uploaded CSV', 'error');
        return;
      }

      const prefill = buildPoPrefillFromPlanningShortage(uploadContext, woDisplayLabel(uploadContext)) || {
        orderTo: '',
        billTo: '',
        shipTo: '',
        operatorId: '',
        aircraftId: uploadContext.aircraftId?._id || uploadContext.aircraftId || '',
        aircraftRegistration: uploadContext.aircraftId?.registration || '',
        aircraftModelVariant: uploadContext.aircraftId?.model || '',
        aircraftSerialNumber: uploadContext.aircraftId?.serialNumber || '',
        expectedDeliveryDate: '',
        priority: 'MEDIUM',
        notes: `Uploaded from procurement CSV for ${woDisplayLabel(uploadContext)}`,
        lineItems: []
      };
      prefill.lineItems = lineItems.map((line, idx) => ({
        lineNumber: idx + 1,
        partNumber: line.partNumber,
        description: line.description,
        quantity: line.quantity,
        unit: 'EACH',
        remarks: `CSV import — WO ${woDisplayLabel(uploadContext)}`.trim()
      }));
      openProcurementFormWithPrefill(prefill);
    } catch (err) {
      console.error(err);
      showToast('Failed to upload procurement CSV', 'error');
    } finally {
      event.target.value = '';
      setUploadContext(null);
    }
  }, [openProcurementFormWithPrefill, showToast, uploadContext]);

  const handleComponentsDialogUpdate = useCallback(async () => {
    if (!componentsDialogWo?._id) return;
    try {
      const response = await axios.get(`/api/maintenance/${componentsDialogWo._id}`);
      const updated = response.data;
      setComponentsDialogWo(updated);
      setStorePlanningQueue((prev) =>
        prev.map((row) => (row._id === updated._id ? { ...row, ...updated } : row))
      );
    } catch (e) {
      console.error(e);
      showToast('Could not refresh work order after updating components.', 'error');
    }
  }, [componentsDialogWo?._id, showToast]);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      const maintenanceRes = await axios.get('/api/maintenance');
      const maintenanceAll = Array.isArray(maintenanceRes.data) ? maintenanceRes.data : [];
      const storeQueue = maintenanceAll.filter(
        (m) =>
          m.planningApproved &&
          m.needsStoreAction &&
          (m.status === 'SCHEDULED' || m.status === 'COMPLETED' || m.storeAccepted)
      );
      // Independent Planning requests are handled on the Independent PO tab.
      const mergedQueue = [...storeQueue];
      const needsProcurementSync = storeQueue.filter(
        (wo) => countWorkOrderSparesConsumables(wo) > 0 && !hasProcurementSnapshot(wo)
      );
      const needsAvailabilityRefresh = storeQueue.filter(
        (wo) => hasProcurementSnapshot(wo) && !needsProcurementSync.some((row) => row._id === wo._id)
      );
      const syncedById = new Map();
      if (needsProcurementSync.length > 0) {
        const synced = await Promise.all(
          needsProcurementSync.map(async (wo) => {
            try {
              const { data } = await axios.post(
                `/api/maintenance/${wo._id}/sync-procurement-from-components`
              );
              return data;
            } catch (syncErr) {
              console.error('Procurement sync failed for WO', wo._id, syncErr);
              return wo;
            }
          })
        );
        synced.forEach((wo) => syncedById.set(wo._id, wo));
      }
      if (needsAvailabilityRefresh.length > 0) {
        try {
          const { data } = await axios.post('/api/maintenance/refresh-procurement-availability', {
            ids: needsAvailabilityRefresh.map((wo) => wo._id)
          });
          (data?.workOrders || []).forEach((wo) => syncedById.set(wo._id, wo));
        } catch (refreshErr) {
          console.error('Procurement availability refresh failed', refreshErr);
        }
      }
      if (syncedById.size > 0) {
        setStorePlanningQueue(
          mergedQueue.map((row) => syncedById.get(row._id) || row)
        );
      } else {
        setStorePlanningQueue(mergedQueue);
      }
    } catch (e) {
      if (e.response?.status !== 403) {
        console.error(e);
        showToast('Could not load work orders for store review.', 'error');
      }
      setStorePlanningQueue([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const aircraftFilterOptions = useMemo(() => {
    const byValue = new Map();
    storePlanningQueue.forEach((wo) => {
      const value = getIntentAircraftFilterValue(wo);
      if (!byValue.has(value)) {
        byValue.set(value, getIntentAircraftLabel(wo));
      }
    });
    return [...byValue.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [storePlanningQueue]);

  const filteredQueue = useMemo(() => {
    const fromTime = updatedFrom ? new Date(`${updatedFrom}T00:00:00`).getTime() : null;
    const toTime = updatedTo ? new Date(`${updatedTo}T23:59:59.999`).getTime() : null;

    return storePlanningQueue.filter((wo) => {
      const isCompleted = isStoreIntentCompleted(wo);
      const hasPartRequired = hasMaterialIntent(wo);
      const updatedTime = getLastUpdatedTime(wo);

      if (pendingFilter === 'PENDING' && isCompleted) return false;
      if (pendingFilter === 'COMPLETED' && !isCompleted) return false;

      if (partRequiredFilter === 'YES' && !hasPartRequired) return false;
      if (partRequiredFilter === 'NO' && hasPartRequired) return false;

      if (aircraftFilter !== 'ALL' && getIntentAircraftFilterValue(wo) !== aircraftFilter) {
        return false;
      }

      if (fromTime && updatedTime < fromTime) return false;
      if (toTime && updatedTime > toTime) return false;

      return true;
    });
  }, [storePlanningQueue, pendingFilter, partRequiredFilter, aircraftFilter, updatedFrom, updatedTo]);

  const sortedFilteredQueue = useMemo(
    () => sortIntentQueue(filteredQueue, sortField, sortDirection),
    [filteredQueue, sortField, sortDirection]
  );

  if (loading) {
    return <Typography>Loading WO requirements…</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            WO Requirement
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Work order spares and consumables update the same procurement snapshot as Planning (required,
            available, to procure). Newest requirements appear first. Create purchase orders for shortages
            and store accept or reject.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchQueue} aria-label="Refresh list">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ mb: 2 }}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="intent-pending-filter-label">Pending</InputLabel>
            <Select
              labelId="intent-pending-filter-label"
              value={pendingFilter}
              label="Pending"
              onChange={(e) => setPendingFilter(e.target.value)}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="intent-part-filter-label">Part Required</InputLabel>
            <Select
              labelId="intent-part-filter-label"
              value={partRequiredFilter}
              label="Part Required"
              onChange={(e) => setPartRequiredFilter(e.target.value)}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="YES">Yes</MenuItem>
              <MenuItem value="NO">No</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="intent-aircraft-filter-label">Aircraft</InputLabel>
            <Select
              labelId="intent-aircraft-filter-label"
              value={aircraftFilter}
              label="Aircraft"
              onChange={(e) => setAircraftFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Aircraft</MenuItem>
              {aircraftFilterOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Last Updated From"
            type="date"
            value={updatedFrom}
            onChange={(e) => setUpdatedFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="Last Updated To"
            type="date"
            value={updatedTo}
            onChange={(e) => setUpdatedTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="intent-sort-field-label">Sort by</InputLabel>
            <Select
              labelId="intent-sort-field-label"
              value={sortField}
              label="Sort by"
              onChange={(e) => setSortField(e.target.value)}
            >
              {INTENT_SORT_FIELDS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }} disabled={sortField === 'defaultQueue'}>
            <InputLabel id="intent-sort-dir-label">Direction</InputLabel>
            <Select
              labelId="intent-sort-dir-label"
              value={sortDirection}
              label="Direction"
              onChange={(e) => setSortDirection(e.target.value)}
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {storePlanningQueue.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No WO requirements found for store.
          </Typography>
        ) : sortedFilteredQueue.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No WO requirements match the selected filters.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>WO</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Aircraft</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Planning parts &amp; availability</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedFilteredQueue.map((wo) => {
                  const req = wo.partsProcurementRequirements;
                  const hasReq = hasProcurementSnapshot(wo);
                  const componentCount = countWorkOrderComponents(wo);
                  const toolsCount = countWorkOrderTools(wo);
                  const spareConsumableCount = countWorkOrderSparesConsumables(wo);
                  const tools = Array.isArray(wo.requiredToolsEquipment) ? wo.requiredToolsEquipment : [];
                  const prefill = isIndependentIntent(wo)
                    ? buildPoPrefillFromIndependentIntent(wo)
                    : buildPoPrefillFromPlanningShortage(wo, woDisplayLabel(wo));
                  const candidate = getWorkOrderCandidateNumber(wo, displayContext);
                  const canPo = canCreatePo && Boolean(prefill);
                  const lastUpdatedMs = getLastUpdatedTime(wo);
                  return (
                    <TableRow key={wo._id}>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                          <span>{woDisplayLabel(wo) || '—'}</span>
                          {candidate ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={displayContext === 'mro' ? `WO: ${candidate}` : `MRO: ${candidate}`}
                            />
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>{wo.title}</TableCell>
                      <TableCell>{wo.aircraftId?.registration || wo.aircraftRegistration || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatIntentDate(lastUpdatedMs)}
                      </TableCell>
                      <TableCell>
                        {isIndependentIntent(wo) && (
                          <Chip
                            size="small"
                            color="info"
                            label="Planning Independent (Non-WO)"
                            sx={{ mb: 1, display: 'block', width: 'fit-content' }}
                          />
                        )}
                        {wo.storeRejectedAt && (
                          <Chip
                            size="small"
                            color="warning"
                            label="Store rejected — with Planning"
                            sx={{ mb: 1, display: 'block', width: 'fit-content' }}
                          />
                        )}
                        {spareConsumableCount > 0 && (
                          <Chip
                            size="small"
                            label={`${wo.requiredSpares?.length || 0} spare(s), ${wo.requiredConsumables?.length || 0} consumable(s)`}
                            sx={{ mb: 1, mr: 1 }}
                          />
                        )}
                        {toolsCount > 0 && (
                          <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            label={`${toolsCount} tool(s)`}
                            sx={{ mb: 1, mr: 1 }}
                          />
                        )}
                        {!hasReq && componentCount === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            No spares, consumables, tools, or planning procurement lines yet.
                          </Typography>
                        )}
                        {!hasReq && spareConsumableCount > 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Components are listed on the work order; refresh the queue if procurement lines do not
                            appear yet.
                          </Typography>
                        )}
                        {toolsCount > 0 && (
                          <Table size="small" sx={{ minWidth: 320, mb: hasReq ? 1 : 0 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Tool / equipment</TableCell>
                                <TableCell>Part</TableCell>
                                <TableCell align="right">Qty</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {tools.map((tool, idx) => (
                                <TableRow key={`${wo._id}-tool-${tool._id || idx}`}>
                                  <TableCell>{tool.itemName || '—'}</TableCell>
                                  <TableCell sx={{ fontFamily: 'monospace' }}>
                                    {tool.partNumber || '—'}
                                  </TableCell>
                                  <TableCell align="right">{tool.quantity || 1}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                        {hasReq && (
                          <Table size="small" sx={{ minWidth: 360 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Part</TableCell>
                                <TableCell align="right">Required</TableCell>
                                <TableCell align="right">Available (snapshot)</TableCell>
                                <TableCell align="right">To procure</TableCell>
                                <TableCell>Description</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {req.partNumbers.map((pn, idx) => (
                                <TableRow key={`${wo._id}-${pn}-${idx}`}>
                                  <TableCell sx={{ fontFamily: 'monospace' }}>{pn}</TableCell>
                                  <TableCell align="right">{req.quantities?.[idx] ?? '—'}</TableCell>
                                  <TableCell align="right">{req.availableQuantities?.[idx] ?? '—'}</TableCell>
                                  <TableCell align="right">{req.quantitiesToProcure?.[idx] ?? '—'}</TableCell>
                                  <TableCell>{req.descriptions?.[idx] || '—'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                        {hasReq && req.specifications ? (
                          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                            Specs: {req.specifications}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="column" spacing={1} alignItems="flex-end">
                          <Button
                            component={Link}
                            to={isIndependentIntent(wo) ? '/planning' : '/maintenance'}
                            state={isIndependentIntent(wo) ? undefined : { selectedWorkOrderId: wo._id }}
                            size="small"
                            variant="outlined"
                          >
                            {isIndependentIntent(wo) ? 'Open in Planning' : 'Open in Maintenance'}
                          </Button>
                          {!isIndependentIntent(wo) && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Build />}
                              onClick={() => setComponentsDialogWo(wo)}
                            >
                              Spares, consumables &amp; tools
                            </Button>
                          )}
                          {hasReq && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Download />}
                              onClick={() => downloadRequirementsCsv(wo)}
                            >
                              Download Req CSV
                            </Button>
                          )}
                          {hasReq && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<UploadFile />}
                              onClick={() => handleUploadClick(wo)}
                            >
                              Upload CSV to PO
                            </Button>
                          )}
                          <Tooltip
                            title={
                              !canCreatePo
                                ? 'You do not have permission to create purchase orders.'
                                : !prefill
                                ? 'No shortage quantities at last planning snapshot — nothing to order.'
                                : ''
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                disabled={!canPo}
                                onClick={() => openProcurementFormWithPrefill(prefill)}
                              >
                                Create PO (shortages)
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              !canCreatePo
                                ? 'You do not have permission to add purchase orders.'
                                : !prefill
                                ? 'No shortage quantities at last planning snapshot — nothing to order.'
                                : ''
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                disabled={!canPo}
                                onClick={() => openProcurementFormWithPrefill(prefill)}
                              >
                                Add PO
                              </Button>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              isIndependentIntent(wo)
                                ? 'Independent planning requests are not part of Store accept/reject WO workflow.'
                                : !permissions.canStoreAction(wo)
                                ? 'Only Store Inspector (or accountable override) can accept or reject when the work order is in the store queue.'
                                : ''
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={isIndependentIntent(wo) || !permissions.canStoreAction(wo)}
                                onClick={() => setStoreActionWo(wo)}
                              >
                                Store accept / reject
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={Boolean(componentsDialogWo)}
        onClose={() => setComponentsDialogWo(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          WO material &amp; tools — {woDisplayLabel(componentsDialogWo)}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Spares and consumables update procurement requirements (required, available snapshot, and quantities to
            procure). Tools are listed for Store issue / availability and do not create PO shortages.
          </Typography>
          {componentsDialogWo && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Required spares
                </Typography>
                <RequiredSparesManager
                  workOrderId={componentsDialogWo._id}
                  workOrderLabel={woDisplayLabel(componentsDialogWo)}
                  items={componentsDialogWo.requiredSpares}
                  onUpdate={handleComponentsDialogUpdate}
                />
              </Box>
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Required consumables
                </Typography>
                <RequiredConsumablesManager
                  workOrderId={componentsDialogWo._id}
                  workOrderLabel={woDisplayLabel(componentsDialogWo)}
                  items={componentsDialogWo.requiredConsumables}
                  onUpdate={handleComponentsDialogUpdate}
                />
              </Box>
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Required tools &amp; equipment
                </Typography>
                <RequiredToolsEquipmentManager
                  workOrderId={componentsDialogWo._id}
                  workOrderLabel={woDisplayLabel(componentsDialogWo)}
                  items={componentsDialogWo.requiredToolsEquipment}
                  onUpdate={handleComponentsDialogUpdate}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComponentsDialogWo(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <StoreActions
        open={Boolean(storeActionWo)}
        onClose={() => setStoreActionWo(null)}
        workOrder={storeActionWo}
        onSuccess={() => {
          setStoreActionWo(null);
          fetchQueue();
        }}
      />
      <input
        type="file"
        ref={uploadCsvInputRef}
        onChange={handleUploadCsv}
        accept=".csv,text/csv"
        style={{ display: 'none' }}
      />
    </Box>
  );
};

export default StoreWorkOrderRequests;
